import {
  Check,
  ChartColumn,
  ChartLine,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  File,
  FileText,
  Gamepad2,
  Image as LImage,
  LayoutGrid,
  ListChecks,
  type LucideProps,
  Menu,
  MessageSquare,
  MessageSquareText,
  MessagesSquare,
  Moon,
  Music,
  Play,
  Plus,
  RefreshCw,
  Reply,
  Sun,
  Tag,
  TriangleAlert,
  Users,
  Video as LVideo,
  Workflow,
  X,
} from 'lucide-react';
import { forwardRef, type SVGProps } from 'react';

// ---------------------------------------------------------------------------
// PB icon shim
//
// The portal used to ship hand-rolled <svg> components for every glyph.
// We now adopt lucide-react as the source of truth so future icon work is
// a one-line swap. Consumer imports stay stable - every name the portal
// used before (`OverviewIcon`, `FeedbackIcon`, ...) still resolves to the
// same component path, it just renders a lucide icon underneath.
//
// `wrap` locks in the size and stroke weight the prior hand-rolled glyphs
// had so the on-screen pixel weight doesn't shift after the swap. Each
// caller can still override either prop in the usual way.
// ---------------------------------------------------------------------------

type LucideComponent = React.ForwardRefExoticComponent<
  Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
>;

function wrap(
  Component: LucideComponent,
  defaults: Pick<LucideProps, 'size' | 'strokeWidth'>,
): React.ForwardRefExoticComponent<LucideProps & React.RefAttributes<SVGSVGElement>> {
  const Wrapped = forwardRef<SVGSVGElement, LucideProps>((props, ref) => (
    <Component ref={ref} {...defaults} {...props} />
  ));
  Wrapped.displayName = `PB(${Component.displayName ?? 'Icon'})`;
  return Wrapped;
}

// Defaults mirror the prior navBaseProps - 16px glyph at strokeWidth 1.5.
const NAV: Pick<LucideProps, 'size' | 'strokeWidth'> = { size: 16, strokeWidth: 1.5 };
// Theme glyphs were 14px in the prototype.
const THEME: Pick<LucideProps, 'size' | 'strokeWidth'> = { size: 14, strokeWidth: 1.5 };
// Inline UI glyphs that sat at 12px (Check, Plus, ChevronDown).
const COMPACT: Pick<LucideProps, 'size' | 'strokeWidth'> = { size: 12, strokeWidth: 1.6 };
// MenuIcon / CloseIcon were 16 / 14 with strokeWidth 1.6 in the prototype.
const MENU: Pick<LucideProps, 'size' | 'strokeWidth'> = { size: 16, strokeWidth: 1.6 };
const CLOSE: Pick<LucideProps, 'size' | 'strokeWidth'> = { size: 14, strokeWidth: 1.6 };

// Sidebar nav glyphs.
export const OverviewIcon = wrap(LayoutGrid, NAV);
export const InsightsIcon = wrap(ChartLine, NAV);
export const PlaytestsIcon = wrap(Play, NAV);
export const FeedbackIcon = wrap(MessageSquare, NAV);
export const ImprovementLogIcon = wrap(ListChecks, NAV);
export const PlaytestSettingsIcon = wrap(Workflow, NAV);
export const FeedbackSettingsIcon = wrap(MessageSquareText, NAV);
export const GameSettingsIcon = wrap(Gamepad2, NAV);
export const StudioIcon = wrap(Users, NAV);

// Utility glyphs.
export const ChevronDownIcon = wrap(ChevronDown, COMPACT);
export const ChevronLeftIcon = wrap(ChevronLeft, COMPACT);
export const ChevronRightIcon = wrap(ChevronRight, COMPACT);
export const CheckIcon = wrap(Check, COMPACT);
export const PlusIcon = wrap(Plus, COMPACT);
export const MenuIcon = wrap(Menu, MENU);
export const CloseIcon = wrap(X, CLOSE);

// Theme glyphs.
export const SunIcon = wrap(Sun, THEME);
export const MoonIcon = wrap(Moon, THEME);

// Attachment glyphs.
export const ImageIcon = wrap(LImage, NAV);
export const VideoIcon = wrap(LVideo, NAV);
export const AudioIcon = wrap(Music, NAV);
export const FileIcon = wrap(File, NAV);

// Content-shape glyphs.
export const PollIcon = wrap(ChartColumn, NAV);
export const FormIcon = wrap(FileText, NAV);
export const ReplyIcon = wrap(Reply, NAV);
export const ThreadIcon = wrap(MessagesSquare, NAV);
export const TagIcon = wrap(Tag, NAV);
export const WarningIcon = wrap(TriangleAlert, NAV);

// Insights refresh affordance - lucide RefreshCw at the same 14px the
// prior inline spinner used.
export const RefreshIcon = wrap(RefreshCw, { size: 14, strokeWidth: 2 });

// Discord brand mark. SVG path is the canonical Clyde mark from
// discord.com/branding, on its native 127.14x96.36 viewBox. Aspect ratio is
// ~1.32:1 - call sites should use `h-N w-auto` (not `h-N w-N`) so the mark
// is not squashed to square. Preserved as hand-rolled because lucide has
// no Discord mark and Discord's brand guidelines require the official
// Clyde silhouette on connect-to-Discord buttons.
export function DiscordIcon(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      width={21}
      height={16}
      viewBox="0 0 127.14 96.36"
      fill="currentColor"
      aria-hidden
      {...props}
    >
      <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
    </svg>
  );
}
